import { useMemo, memo, type ReactNode } from 'react';
import clsx from 'clsx';
import { useTargetBrandConfig } from 'FrontRoyalAngular';
import { type CurrentUser } from 'Users';
import { type AppMenuUserAvatarPresentationProps } from './AppMenuUserAvatar.types';

type AppMenuUserAvatarProps = {
    user: CurrentUser | null;
    children?: ReactNode;
    className?: string;
};

// Presentation component - renders avatar based on explicit props
export const AppMenuUserAvatarPresentation = memo(
    ({ avatarUrl, brandStyleClass, children, className = '' }: AppMenuUserAvatarPresentationProps) => {
        const style = useMemo(
            () => ({
                backgroundImage: avatarUrl ? `url(${avatarUrl})` : undefined,
            }),
            [avatarUrl],
        );

        // From SCSS: $extraPadding: 5px, $iconSize: 37px
        return (
            <div
                className={clsx(
                    'app-menu-user-avatar',
                    brandStyleClass,
                    className,
                    // Tailwind - match app-menu-user-avatar SCSS
                    'absolute',
                    'right-[5px]', // $extraPadding
                    'top-[5px]', // $extraPadding
                    'w-[37px]', // $iconSize
                    'h-[37px]', // $iconSize
                    'rounded-full',
                    'bg-cover',
                    'bg-center',
                    'bg-no-repeat',
                    'rtl:left-[5px]',
                    'rtl:right-auto',
                )}
                style={style}
            >
                {children}
            </div>
        );
    },
);

// Container component - handles hooks and passes data to presentation
export const AppMenuUserAvatar = memo(({ user, children, className = '' }: AppMenuUserAvatarProps) => {
    const targetBrandConfig = useTargetBrandConfig();

    const avatarUrl = useMemo(() => {
        if (user?.avatarUrl) {
            return user.avatarUrl;
        }
        // Fall back to brand default avatar
        return targetBrandConfig.avatarDefault;
    }, [user?.avatarUrl, targetBrandConfig.avatarDefault]);

    return (
        <AppMenuUserAvatarPresentation
            avatarUrl={avatarUrl}
            brandStyleClass={targetBrandConfig.brandStyleClass}
            className={className}
        >
            {children}
        </AppMenuUserAvatarPresentation>
    );
});
